// HK calendar-date helpers for the browser. API date columns are HK-midnight
// instants; these derive HK calendar components from the instant so the UI is
// correct regardless of the device timezone (user devices are in Asia/Hong_Kong,
// but CI/other zones must not shift dates). HK has no DST.
const HK_OFFSET_MS = 8 * 3600 * 1000;
const pad2 = (n: number) => String(n).padStart(2, "0");

/** HK calendar date (YYYY-MM-DD) for an instant or ISO string, TZ-independent. */
export function hkDateOf(input: Date | string): string {
  const d = typeof input === "string" ? new Date(input) : input;
  const hk = new Date(d.getTime() + HK_OFFSET_MS);
  return `${hk.getUTCFullYear()}-${pad2(hk.getUTCMonth() + 1)}-${pad2(hk.getUTCDate())}`;
}

/** Today's HK calendar date (YYYY-MM-DD). */
export function hkToday(): string {
  return hkDateOf(new Date());
}

/** Shift a YYYY-MM-DD HK date by whole days (TZ-independent). */
export function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d! + days));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/** Short display for a YYYY-MM-DD HK date, e.g. "22 Aug" (TZ-independent). */
export function formatHkDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.toLocaleDateString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short" });
}

/** ICP frequency values (mirror the API's residents.icpFrequency). */
export type IcpFrequency = "1m" | "6m" | "1y";

const FREQ_MONTHS: Record<IcpFrequency, number> = { "1m": 1, "6m": 6, "1y": 12 };

/** 1m -> 6m -> 1y -> 1y; null/undefined (first assignment) -> 1m. Mirrors the server. */
export function advanceIcpFrequency(freq: IcpFrequency | null | undefined): IcpFrequency {
  if (freq === "1m") return "6m";
  if (freq === "6m") return "1y";
  if (freq === null || freq === undefined) return "1m";
  return "1y"; // freq === "1y" stays at "1y"
}

/** anchor + freq - 1 day, month-end normalized like the sheet's DATE() formula (server lib/deadlines.ts). */
export function nextIcpDeadline(anchor: string, freq: IcpFrequency): string {
  const [y, m, d] = anchor.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1 + FREQ_MONTHS[freq], d! - 1));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}
